import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import {
  FileArchive,
  Upload,
  Download,
  Unlink,
  Search,
  Plus,
  Loader,
  Trash2,
  Pencil,
  Check,
  X,
  AlertCircle,
} from 'lucide-react';
import { buildTeamApiService } from '../../services/buildTeamApi';
import type { BuildSchematic, BuildSchematicBulkUploadResult, BuildSchematicFileKind } from '../../types/buildTeam';
import { useBuildSchematicBlobUrl } from '../../hooks/useBuildSchematicBlobUrl';
import { schematicDisplayTitle, schematicShowsFilename } from '../../utils/buildSchematic';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import { SchematicDeliveryAction } from './SchematicDeliveryAction';

export interface BuildSchematicsSectionProps {
  targetId: string;
  targetType: 'project' | 'build';
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function schematicKindLabel(kind: BuildSchematicFileKind | undefined): string {
  return kind === 'screenshot' ? 'Screenshot' : 'Schematic';
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

/** Inline preview of a screenshot stored beside the schematics. */
export function SchematicScreenshotPreview({ schematicId, alt, className }: { schematicId: string; alt: string; className?: string }) {
  const { url, loading, error } = useBuildSchematicBlobUrl(schematicId);
  if (loading) {
    return <SkeletonLoader className={cn('h-24 w-full rounded-lg', className)} />;
  }
  if (error || !url) {
    return (
      <div className={cn('flex items-center justify-center h-24 text-xs text-glass-500 bg-glass-100/5 rounded-lg border border-glass-200/10 px-2 text-center', className)}>
        Preview unavailable
      </div>
    );
  }
  return (
    <div className={cn('rounded-lg overflow-hidden border border-glass-200/10 bg-dark-900/30 max-h-40', className)}>
      <img src={url} alt={alt} className="w-full max-h-40 object-contain" loading="lazy" />
    </div>
  );
}

/** Edit the title, version and description of a schematic or screenshot in place. */
export function SchematicEditForm({
  schematic,
  onSaved,
  onCancel,
}: {
  schematic: BuildSchematic;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(schematic.title ?? '');
  const [version, setVersion] = useState(schematic.version ?? '');
  const [description, setDescription] = useState(schematic.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await buildTeamApiService.updateSchematic(schematic.id, {
        title: title.trim() || null,
        version: version.trim() || null,
        description: description.trim() || null,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update file');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="subcard p-3 space-y-2">
      <div className="flex items-center gap-2 min-w-0">
        <FileArchive className="w-4 h-4 text-accent-300 shrink-0" />
        <span className="text-sm font-medium text-white truncate">{schematicDisplayTitle(schematic)}</span>
        <span className="badge badge-neutral shrink-0">{schematicKindLabel(schematic.file_kind)}</span>
      </div>
      <div>
        <label htmlFor={`schematic-title-${schematic.id}`} className={LABEL_CLASS}>Title</label>
        <input
          id={`schematic-title-${schematic.id}`}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder={schematicDisplayTitle({ title: null, filename: schematic.filename })}
          className="glass-input w-full"
          autoFocus
        />
        <p className="text-xs text-glass-500 mt-1">
          Shown everywhere instead of the filename. The file itself stays <span className="font-mono">{schematic.filename}</span>.
        </p>
      </div>
      <div>
        <label htmlFor={`schematic-version-${schematic.id}`} className={LABEL_CLASS}>Version</label>
        <input
          id={`schematic-version-${schematic.id}`}
          type="text"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="Optional, e.g. v2 or final"
          className="glass-input w-full"
        />
      </div>
      <div>
        <label htmlFor={`schematic-description-${schematic.id}`} className={LABEL_CLASS}>Description</label>
        <textarea
          id={`schematic-description-${schematic.id}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional notes: what it is, where it goes, what still needs doing"
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="glass-button-secondary">
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save
        </button>
      </div>
    </form>
  );
}

export function BuildSchematicsSection({
  targetId,
  targetType,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: BuildSchematicsSectionProps) {
  const [schematics, setSchematics] = useState<BuildSchematic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);

  const [confirmAction, setConfirmAction] = useState<{ type: 'unlink' | 'delete'; id: string } | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BuildSchematic[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const loadSchematics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        targetType === 'project'
          ? await buildTeamApiService.getProjectSchematics(targetId)
          : await buildTeamApiService.getBuildSchematics(targetId);
      setSchematics(data);
      const uploaderIds = data.map((s) => s.uploader_id).filter(Boolean);
      usernameCacheService.prefetchUsernames(uploaderIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schematics');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    loadSchematics();
  }, [loadSchematics]);

  const linkedIds = useMemo(() => new Set(schematics.map((s) => s.id)), [schematics]);

  useEffect(() => {
    if (!showLinkSearch) {
      setSearchResults([]);
      setSearchQuery('');
      return;
    }
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const t = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await buildTeamApiService.searchSchematics(q);
        setSearchResults(results.filter((r) => !linkedIds.has(r.id)));
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchQuery, showLinkSearch, linkedIds]);

  const handleDownload = async (schematic: BuildSchematic) => {
    if (downloadingId) return;
    setDownloadingId(schematic.id);
    try {
      const res = await buildTeamApiService.downloadSchematic(schematic.id);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = schematic.filename || 'schematic';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUnlink = async (schematicId: string) => {
    if (!canEdit || unlinkingId) return;
    setConfirmAction({ type: 'unlink', id: schematicId });
  };

  const handleDelete = async (schematicId: string) => {
    if (!canDelete || deletingId) return;
    setConfirmAction({ type: 'delete', id: schematicId });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'unlink') {
      setUnlinkingId(confirmAction.id);
      try {
        if (targetType === 'project') {
          await buildTeamApiService.unlinkSchematicFromProject(targetId, confirmAction.id);
        } else {
          await buildTeamApiService.unlinkSchematicFromBuild(targetId, confirmAction.id);
        }
        await loadSchematics();
        onUpdate();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to unlink schematic');
      } finally {
        setUnlinkingId(null);
        setConfirmAction(null);
      }
    } else if (confirmAction.type === 'delete') {
      setDeletingId(confirmAction.id);
      try {
        await buildTeamApiService.deleteSchematic(confirmAction.id);
        await loadSchematics();
        onUpdate();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to delete schematic');
      } finally {
        setDeletingId(null);
        setConfirmAction(null);
      }
    }
  };


  const handleLinkExisting = async (schematicId: string) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      if (targetType === 'project') {
        await buildTeamApiService.linkSchematicToProject(targetId, schematicId);
      } else {
        await buildTeamApiService.linkSchematicToBuild(targetId, schematicId);
      }
      setShowLinkSearch(false);
      await loadSchematics();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link schematic');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-28" />
          <SkeletonLoader className="h-9 w-48" />
        </div>
        <div className="divide-y divide-glass-200/10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3 first:pt-0 space-y-2">
              <div className="flex items-center gap-2">
                <SkeletonLoader className="h-4 w-4 rounded" />
                <SkeletonLoader className="h-4 w-48" />
                <SkeletonLoader className="h-5 w-16 rounded-full" />
              </div>
              <SkeletonLoader className="h-3 w-56" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load schematics"
        description={error}
        action={
          <button type="button" onClick={loadSchematics} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Schematics & screenshots"
        icon={FileArchive}
        count={schematics.length}
        className="mb-0"
        description={canDelete ? undefined : canEdit ? 'Edit a title or unlink a file from here. Deleting a file for good needs a build admin.' : undefined}
        actions={
          canEdit || canCreate ? (
            <>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowLinkSearch(!showLinkSearch)}
                  className="glass-button-secondary"
                  aria-expanded={showLinkSearch}
                >
                  <Search className="w-4 h-4" />
                  Link existing
                </button>
              )}
              {canCreate && (
                <button
                  type="button"
                  data-tour="build-schematic-upload"
                  onClick={() => setShowUpload(!showUpload)}
                  className="glass-button"
                  aria-expanded={showUpload}
                >
                  <Plus className="w-4 h-4" />
                  Upload
                </button>
              )}
            </>
          ) : undefined
        }
      />

      {showUpload && canCreate && (
        <SchematicUploadForm
          targetId={targetId}
          targetType={targetType}
          onDone={async () => {
            setShowUpload(false);
            await loadSchematics();
            onUpdate();
          }}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {showLinkSearch && canEdit && (
        <div className="subcard p-4 space-y-3">
          <div>
            <label className={LABEL_CLASS}>Search schematics</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type at least 2 characters…"
                className="glass-input w-full pl-9"
              />
            </div>
          </div>
          {searchLoading && (
            <p className="text-glass-500 text-xs flex items-center gap-2">
              <Loader className="w-4 h-4 animate-spin" /> Searching…
            </p>
          )}
          {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
            <p className="text-glass-500 text-xs">No matches (or all already linked).</p>
          )}
          {searchResults.length > 0 && (
            <ul className="max-h-48 overflow-y-auto rounded-lg border border-glass-200/10 divide-y divide-glass-200/10">
              {searchResults.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 py-2 px-3"
                >
                  <div className="min-w-0 text-sm">
                    <span className="text-sm text-white truncate block">{schematicDisplayTitle(s)}</span>
                    {schematicShowsFilename(s) && <span className="font-mono text-xs text-glass-500 truncate block">{s.filename}</span>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                      <span className="badge badge-neutral">
                        {schematicKindLabel(s.file_kind)}
                      </span>
                      {s.version && <span className="text-xs text-glass-500 font-mono">v{s.version}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => handleLinkExisting(s.id)}
                    className="glass-button-sm shrink-0"
                  >
                    Link
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {schematics.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={FileArchive}
          title="No schematics or screenshots yet"
          description={canCreate ? 'Upload a .schem file or a screenshot, give it a title, and it is linked here.' : 'Linked schematics and screenshots will appear here.'}
          action={canCreate && !showUpload ? <button type="button" className="btn-primary" onClick={() => setShowUpload(true)}><Upload className="w-4 h-4" />Upload a file</button> : undefined}
        />
      ) : (
        <ul className="divide-y divide-glass-200/10">
          {schematics.map((s) => (
            <li
              key={s.id}
              className="py-3 first:pt-0 last:pb-0"
            >
              {editingId === s.id ? (
                <SchematicEditForm
                  schematic={s}
                  onSaved={async () => {
                    setEditingId(null);
                    await loadSchematics();
                    onUpdate();
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0 space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileArchive className="w-4 h-4 text-accent-300 shrink-0" />
                      <span className="text-sm font-medium text-white truncate">{schematicDisplayTitle(s)}</span>
                      <span
                        className={cn(
                          'badge shrink-0',
                          (s.file_kind ?? 'schematic') === 'screenshot' ? 'badge-info' : 'badge-neutral'
                        )}
                      >
                        {schematicKindLabel(s.file_kind)}
                      </span>
                      {s.version && (
                        <span className="badge badge-neutral font-mono">
                          v{s.version}
                        </span>
                      )}
                      <span className="text-xs text-glass-500 tabular-nums">{formatFileSize(s.size)}</span>
                    </div>
                    {schematicShowsFilename(s) && (
                      <p className="font-mono text-xs text-glass-500 truncate" title={s.filename}>{s.filename}</p>
                    )}
                    {(s.file_kind ?? 'schematic') === 'screenshot' && (
                      <SchematicScreenshotPreview schematicId={s.id} alt={schematicDisplayTitle(s)} />
                    )}
                    {s.description && <p className="text-xs text-glass-400 line-clamp-2">{s.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-500">
                      <span className="tabular-nums">
                        Uploaded{' '}
                        <time dateTime={new Date(s.created_at).toISOString()}>
                          {format(new Date(s.created_at), 'PPp')}
                        </time>
                      </span>
                      <span className="flex items-center gap-1">
                        <span>by</span>
                        <CreatorProfile creatorId={s.uploader_id} inline />
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    <SchematicDeliveryAction schematic={s} canSend={canCreate} />
                    <button
                      type="button"
                      onClick={() => handleDownload(s)}
                      disabled={downloadingId === s.id}
                      className="glass-button-sm touch-manipulation"
                      title={(s.file_kind ?? 'schematic') === 'screenshot' ? 'Download image' : 'Download schematic'}
                    >
                      {downloadingId === s.id ? (
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      {(s.file_kind ?? 'schematic') === 'screenshot' ? 'Save file' : 'Download'}
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setEditingId(s.id)}
                        className="glass-button-sm touch-manipulation"
                        title="Edit title, version and description"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleUnlink(s.id)}
                        disabled={unlinkingId === s.id}
                        className="glass-button-sm text-amber-300 hover:text-amber-200 touch-manipulation"
                        title={`Remove from this ${targetType}; the file stays in the library`}
                      >
                        {unlinkingId === s.id ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Unlink className="w-3.5 h-3.5" />
                        )}
                        Unlink
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(s.id)}
                        disabled={deletingId === s.id}
                        className="glass-button-sm text-red-400 hover:text-red-300 touch-manipulation"
                        title="Delete schematic permanently"
                      >
                        {deletingId === s.id ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirmAction && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmAction(null)}
          onConfirm={executeConfirmAction}
          title={confirmAction.type === 'delete' ? 'Delete Schematic' : 'Unlink Schematic'}
          message={confirmAction.type === 'delete'
            ? 'Delete this file for good, everywhere it is linked? This cannot be undone.'
            : `Remove this file from this ${targetType}? It stays in the library and can be linked again.`}
          confirmText={confirmAction.type === 'delete' ? 'Delete' : 'Unlink'}
          isLoading={!!unlinkingId || !!deletingId}
        />
      )}
    </div>
  );
}

/**
 * Upload schematics and screenshots. With a target the files are linked to that
 * project or build as they are created; without one they only join the library.
 */
export function SchematicUploadForm({
  targetId,
  targetType,
  onDone,
  onCancel,
}: {
  targetId?: string;
  targetType?: 'project' | 'build';
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [fileKindOverride, setFileKindOverride] = useState<'' | BuildSchematicFileKind>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: {
      'application/octet-stream': ['.schem', '.schematic', '.nbt', '.litematic'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (acceptedFiles.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      if (acceptedFiles.length === 1) {
        formData.append('file', acceptedFiles[0]);
      } else {
        acceptedFiles.forEach((f, i) => {
          formData.append(`files[${i}]`, f);
        });
      }
      if (title.trim()) formData.append('title', title.trim());
      if (version.trim()) formData.append('version', version.trim());
      if (description.trim()) formData.append('description', description.trim());
      if (fileKindOverride) formData.append('file_kind', fileKindOverride);
      if (targetId && targetType) {
        formData.append('target_id', targetId);
        formData.append('target_type', targetType);
      }

      const result = await buildTeamApiService.uploadSchematic(formData);

      if ('created' in (result as BuildSchematicBulkUploadResult)) {
        const bulk = result as BuildSchematicBulkUploadResult;
        if (bulk.failed.length > 0) {
          alert(
            `Uploaded ${bulk.created.length} file(s). Failed: ${bulk.failed.map((f) => `${f.filename}: ${f.error}`).join('; ')}`
          );
        }
      }

      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const several = acceptedFiles.length > 1;

  return (
    <form
      onSubmit={handleSubmit}
      className="subcard p-4 space-y-3"
      aria-label="Upload schematics or screenshots"
    >
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          isDragActive ? 'border-accent-500/50 bg-accent-500/5' : 'border-glass-200/20 hover:border-glass-200/40'
        )}
      >
        <input {...getInputProps()} />
        <span className="icon-tile icon-tile-neutral mx-auto mb-2">
          <Upload className="w-4 h-4" />
        </span>
        <p className="text-sm text-glass-300">
          {acceptedFiles.length > 0 ? (
            <span className="font-mono text-accent-300">
              {acceptedFiles.length === 1
                ? acceptedFiles[0].name
                : `${acceptedFiles.length} files selected`}
            </span>
          ) : isDragActive ? (
            'Drop files here'
          ) : (
            'Choose files or drag them here'
          )}
        </p>
        <p className="text-xs text-glass-500 mt-1">
          Schematics (.schem, .schematic, .nbt, .litematic) and screenshots (.png, .jpg, .gif, .webp). Several at once is fine.
        </p>
      </div>

      <div>
        <label htmlFor="schematic-upload-title" className={LABEL_CLASS}>
          Title{several ? ' (applies to every file in this upload)' : ''}
        </label>
        <input
          id="schematic-upload-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder={acceptedFiles.length === 1 ? schematicDisplayTitle({ title: null, filename: acceptedFiles[0].name }) : 'What the team should call it, e.g. Spawn plaza'}
          className="glass-input w-full"
        />
        <p className="text-xs text-glass-500 mt-1">
          This is the name shown in the library and on {targetType ?? 'every'} pages. Leave it blank to use the filename.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="schematic-upload-version" className={LABEL_CLASS}>Version</label>
          <input
            id="schematic-upload-version"
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="Optional, e.g. v2 or final"
            className="glass-input w-full"
          />
        </div>
        <div>
          <label htmlFor="schematic-upload-kind" className={LABEL_CLASS}>File kind</label>
          <select
            id="schematic-upload-kind"
            value={fileKindOverride}
            onChange={(e) => setFileKindOverride(e.target.value as '' | BuildSchematicFileKind)}
            className="glass-select w-full"
          >
            <option value="">Work it out from each file</option>
            <option value="schematic">Treat all as schematics</option>
            <option value="screenshot">Treat all as screenshots</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="schematic-upload-description" className={LABEL_CLASS}>Description</label>
        <textarea
          id="schematic-upload-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional notes: what it is, where it goes, what still needs doing"
          rows={3}
          className="glass-input w-full resize-none"
        />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || acceptedFiles.length === 0}
          className="btn-primary"
        >
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {targetId ? 'Upload & link' : 'Upload'}
        </button>
      </div>
    </form>
  );
}
